// src/components/requests/RequestsSettings.jsx
import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Palette, 
  Layout, 
  MessageSquare,
  Shield,
  Search,
  Plus, 
  Settings2,
  GripVertical,
  Trash2
} from 'lucide-react';
import { SettingsModal } from '../settings/shared/SettingsModal';
import { TabNavigation } from '../settings/shared/TabNavigation';
import { BasicInfoTab } from '../settings/shared/BasicInfoTab';
import { DesignBrandingSection } from '../settings/shared/DesignSection';
import { HeaderSettings } from '../settings/shared/HeaderSettings';
import { PrivacySection } from '../settings/shared/PrivacySection';
import { SEOSection } from '../settings/shared/SEOSection';
import { firestoreService } from '../../services/firestoreService';

export const RequestsSettings = ({ book, isOpen, onClose, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [uploadStates, setUploadStates] = useState({});
  const [errors, setErrors] = useState({});

  const [logoFile, setLogoFile] = useState(null);
  const [coverImageFile, setCoverImageFile] = useState(null);
  const [headerBackgroundFile, setHeaderBackgroundFile] = useState(null);
  const [faviconFile, setFaviconFile] = useState(null);
  const [ogImageFile, setOgImageFile] = useState(null);

  const [logoPreview, setLogoPreview] = useState('');
  const [coverImagePreview, setCoverImagePreview] = useState('');
  const [headerBackgroundPreview, setHeaderBackgroundPreview] = useState('');
  const [faviconPreview, setFaviconPreview] = useState('');
  const [ogImagePreview, setOgImagePreview] = useState('');

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: FileText },
    { id: 'design', label: 'Design & Styling', icon: Palette },
    { id: 'header', label: 'Header Settings', icon: Layout },
    { id: 'requests', label: 'Requests Config', icon: MessageSquare },
    { id: "provider", label: "Provider Config", icon: Settings2 },
    { id: 'privacy', label: 'Privacy & Access', icon: Shield },
    { id: 'seo', label: 'SEO & Metadata', icon: Search }
  ];

  useEffect(() => {
    initializeFormData();
  }, [book]);

  const initializeFormData = () => {
    setFormData({
      botName: book?.botName || book?.title || '',
      description: book?.description || '',
      botCategory: book?.botCategory || 'requests',
      logoUrl: book?.logoUrl || '',
      coverImageUrl: book?.coverImageUrl || '',
      headerBackgroundUrl: book?.headerBackgroundUrl || '',
      
      published: book?.published !== false,
      
      accessLevel: book?.accessLevel || 'public',
      requireLogin: book?.requireLogin || false,
      allowIndexing: book?.allowIndexing !== false,
      authorizedUsers: book?.authorizedUsers || [],
      allowPrint: book?.allowPrint !== false,
      allowDownload: book?.allowDownload || false,
      enableRightClickProtection: book?.enableRightClickProtection || false,
      passwordProtected: book?.passwordProtected || false,
      accessPassword: book?.accessPassword || '',
      
      primaryColor: book?.styling?.primaryColor || '#6366f1',
      backgroundColor: book?.styling?.backgroundColor || '#ffffff',
      headerColor: book?.styling?.headerColor || '#1e293b',
      headerTextColor: book?.styling?.headerTextColor || '#ffffff',
      headerOpacity: book?.styling?.headerOpacity || '100',
      textColor: book?.styling?.textColor || '#1e293b',
      accentColor: book?.styling?.accentColor || '#8b5cf6',
      fontFamily: book?.styling?.fontFamily || 'Inter, sans-serif',
      logoInvert: book.styling?.logoInvert || 0,
      headerRadius: book?.styling?.headerRadius || '0px',
      cardRadius: book?.styling?.cardRadius || '12px',
      shadowLevel: book?.styling?.shadowLevel || 'medium',
      
      headerSettings: {
        showLogo: book?.headerSettings?.showLogo ?? true,
        showTitle: book?.headerSettings?.showTitle ?? true,
        showDescription: book?.headerSettings?.showDescription ?? false,
        headerHeight: book?.headerSettings?.headerHeight ?? 80,
        logoHeight: book?.headerSettings?.logoHeight ?? 40,
        position: book?.headerSettings?.position || 'sticky',
        layout: book?.headerSettings?.layout || 'default',
        showSearchBar: book?.headerSettings?.showSearchBar ?? true,
        showUserProfile: book?.headerSettings?.showUserProfile ?? true,
        showNotifications: book?.headerSettings?.showNotifications ?? true,
        ...book?.headerSettings
      },
      
      requestsSettings: {
        requestPlaceholder: book?.requestsSettings?.requestPlaceholder || book?.requestPlaceholder || 'Describe what you need…',
        defaultMaxResponses: book?.requestsSettings?.defaultMaxResponses || book?.defaultMaxResponses || 3,
        providerVisibility: book?.requestsSettings?.providerVisibility || book?.providerVisibility || 'all-providers',
        aiSeedPrompt: book?.requestsSettings?.aiSeedPrompt || book?.aiSeedPrompt || '',
        enableAutoMatching: book?.requestsSettings?.enableAutoMatching ?? true,
        requireProviderApproval: book?.requestsSettings?.requireProviderApproval ?? false,
        allowPublicRequests: book?.requestsSettings?.allowPublicRequests ?? true,
        enableRatings: book?.requestsSettings?.enableRatings ?? true,
        enableReviews: book?.requestsSettings?.enableReviews ?? true,
        notifyOnNewRequest: book?.requestsSettings?.notifyOnNewRequest ?? true,
        notifyOnNewResponse: book?.requestsSettings?.notifyOnNewResponse ?? true
      },

      providerFields: book.providerFields || [
        { id: 'businessName', name: 'businessName', type: 'text', label: 'Business Name', required: true, order: 0 },
        { id: 'category', name: 'category', type: 'select', label: 'Category', required: true, order: 1 },
        { id: 'description', name: 'description', type: 'textarea', label: 'Description', required: true, order: 2, rows: 4 },
        { id: 'email', name: 'email', type: 'email', label: 'Email', required: true, order: 3 },
        { id: 'phone', name: 'phone', type: 'tel', label: 'Phone', required: true, order: 4 },
        { id: 'website', name: 'website', type: 'url', label: 'Website', required: false, order: 5 },
        { id: 'logo', name: 'logo', type: 'image', label: 'Logo', required: false, order: 6 }
      ],
      providerCategories: book.providerCategories || [
        "Home Improvement & Repair",
        "Cleaning Services",
        "Plumbing & HVAC",
        "Electrical",
        "Moving & Storage",
        "Web & Graphic Design",
        "Digital Marketing",
        "Tutoring & Education",
        "Events & Catering",
        "Personal Training & Wellness",
        "Legal & Financial",
        "Other"
      ],
      providerSettings: book.providerSettings || {
        requireApproval: true,
        allowMultipleListings: false,
        enableVerifiedBadge: true,
        enableFeatured: true,
        maxListingsPerProvider: 1
      },

      creditsSettings: book.creditsSettings || {
        costPerOffer: 5,
        costPerResponse: 1,
        freeCredits: 50,
        allowAdditionalPurchase: true,
        packages: {
          basic: { credits: 100, price: 100 },
          standard: { credits: 250, price: 225 },
          premium: { credits: 500, price: 400 }
        }
      },
      
      seoTitle: book?.seoTitle || book?.botName || '',
      seoDescription: book?.seoDescription || book?.description || '',
      seoKeywords: book?.seoKeywords || '',
      ogImage: book?.ogImage || '',
      customDomain: book?.customDomain || '',
      customPath: book?.customPath || '',
      favicon: book?.favicon || ''
    });

    setLogoPreview(book?.logoUrl || '');
    setCoverImagePreview(book?.coverImageUrl || '');
    setHeaderBackgroundPreview(book?.headerBackgroundUrl || '');
    setFaviconPreview(book?.favicon || '');
    setOgImagePreview(book?.ogImage || '');
  };

  const handleInputChange = (path, value) => {
    setFormData(prev => {
      if (path.includes('.')) {
        const keys = path.split('.');
        const newData = { ...prev };
        let current = newData;
        
        for (let i = 0; i < keys.length - 1; i++) {
          const key = keys[i];
          if (!current[key] || typeof current[key] !== 'object') {
            current[key] = {};
          } else {
            current[key] = { ...current[key] };
          }
          current = current[key];
        }
        
        current[keys[keys.length - 1]] = value;
        return newData;
      }
      
      return { ...prev, [path]: value };
    });
    setHasChanges(true);
  };

  const handleFileUpload = async (file, fileType) => {
    if (!file || !book?.id) return;

    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, [fileType]: 'File size must be less than 5MB' }));
      return;
    }

    setUploadStates(prev => ({ ...prev, [fileType]: 'uploading' }));
    setErrors(prev => ({ ...prev, [fileType]: null }));

    try {
      const path = `books/${book.id}/${fileType}`;
      const uploadResult = await firestoreService.uploadFile(file, path, book.id);
      
      // Extract URL string from result (handles both string and object responses)
      const urlString = typeof uploadResult === 'string' 
        ? uploadResult 
        : uploadResult?.url || uploadResult;
      
      if (!urlString || typeof urlString !== 'string') {
        throw new Error('Invalid URL returned from upload');
      }

      // Map fileType to formData field
      const fieldMapping = {
        logo: 'logoUrl',
        coverImage: 'coverImageUrl',
        headerBackground: 'headerBackgroundUrl',
        favicon: 'favicon',
        ogImage: 'ogImage'
      };

      const fieldName = fieldMapping[fileType] || fileType;
      handleInputChange(fieldName, urlString);

      // Update preview with the URL string
      const previewSetters = {
        logo: setLogoPreview,
        coverImage: setCoverImagePreview,
        headerBackground: setHeaderBackgroundPreview,
        favicon: setFaviconPreview,
        ogImage: setOgImagePreview
      };

      const previewSetter = previewSetters[fileType];
      if (previewSetter) {
        previewSetter(urlString);
      }

      setUploadStates(prev => ({ ...prev, [fileType]: 'success' }));
      
      setTimeout(() => {
        setUploadStates(prev => ({ ...prev, [fileType]: null }));
      }, 2000);

    } catch (error) {
      console.error('Upload failed:', error);
      setUploadStates(prev => ({ ...prev, [fileType]: 'error' }));
      setErrors(prev => ({ ...prev, [fileType]: error.message || 'Upload failed' }));
    }
  };

  const handleFaviconSelect = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFaviconFile(file);
      setFaviconPreview(URL.createObjectURL(file));
      await handleFileUpload(file, 'favicon');
    }
  };

  const handleOgImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setOgImageFile(file);
      setOgImagePreview(URL.createObjectURL(file));
      await handleFileUpload(file, 'ogImage');
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const updateData = {
        ...book,
        botName: formData.botName,
        title: formData.botName,
        description: formData.description,
        botCategory: formData.botCategory,
        logoUrl: formData.logoUrl,
        coverImageUrl: formData.coverImageUrl,
        headerBackgroundUrl: formData.headerBackgroundUrl,
        published: formData.published,
        
        accessLevel: formData.accessLevel,
        requireLogin: formData.requireLogin,
        allowIndexing: formData.allowIndexing,
        authorizedUsers: formData.authorizedUsers,
        allowPrint: formData.allowPrint,
        allowDownload: formData.allowDownload,
        enableRightClickProtection: formData.enableRightClickProtection,
        passwordProtected: formData.passwordProtected,
        accessPassword: formData.accessPassword,
        
        styling: {
          primaryColor: formData.primaryColor,
          backgroundColor: formData.backgroundColor,
          headerColor: formData.headerColor,
          headerTextColor: formData.headerTextColor,
          headerOpacity: formData.headerOpacity,
          textColor: formData.textColor,
          accentColor: formData.accentColor,
          fontFamily: formData.fontFamily,
          logoInvert: formData.logoInvert,
          headerRadius: formData.headerRadius,
          cardRadius: formData.cardRadius,
          shadowLevel: formData.shadowLevel
        },
        
        headerSettings: formData.headerSettings,
        requestsSettings: formData.requestsSettings,

        providerFields: formData.providerFields,
        providerCategories: formData.providerCategories,
        providerSettings: formData.providerSettings,
        creditsSettings: formData.creditsSettings,
        
        seoTitle: formData.seoTitle,
        seoDescription: formData.seoDescription,
        seoKeywords: formData.seoKeywords,
        ogImage: formData.ogImage,
        customDomain: formData.customDomain,
        customPath: formData.customPath,
        favicon: formData.favicon,
        
        updatedAt: new Date()
      };

      await firestoreService.updateBook(book.id, updateData);
      
      if (onUpdate) {
        onUpdate(updateData);
      }
      
      setHasChanges(false);
      onClose();

    } catch (error) {
      console.error('Error saving settings:', error);
      setErrors({ save: 'Failed to save settings. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return (
          <BasicInfoTab
            formData={formData}
            onInputChange={handleInputChange}
            onFileUpload={handleFileUpload}
            errors={errors}
            bookId={book?.id}
            templateType="requests"
          />
        );

      case 'design':
        return (
          <DesignBrandingSection
            formData={formData}
            onInputChange={handleInputChange}
            bookId={book?.id}
          />
        );

      case 'header':
        return (
          <HeaderSettings
            formData={formData}
            onInputChange={handleInputChange}
            onImageUpload={handleFileUpload}
          />
        );

      case 'requests':
        return (
          <RequestsConfigTab
            formData={formData}
            onInputChange={handleInputChange}
          />
        );

      case 'provider':
        return (
          <ProviderConfigTab 
            formData={formData} 
            onInputChange={handleInputChange}
            setFormData={setFormData}
          />
        );

      case 'privacy':
        return (
          <PrivacySection
            formData={formData}
            onInputChange={handleInputChange}
          />
        );

      case 'seo':
        return (
          <SEOSection
            formData={formData}
            onInputChange={handleInputChange}
            onImageUpload={(file, fileType) => {
              if (fileType === 'favicon') {
                const event = { target: { files: [file] } };
                handleFaviconSelect(event);
              } else if (fileType === 'ogImage') {
                const event = { target: { files: [file] } };
                handleOgImageSelect(event);
              }
            }}
            uploadStates={uploadStates}
            errors={errors}
            bookId={book?.id}
            faviconPreview={faviconPreview}
            ogImagePreview={ogImagePreview}
            onRemoveFavicon={() => {
              setFaviconFile(null);
              setFaviconPreview('');
              handleInputChange('favicon', '');
            }}
            onRemoveOgImage={() => {
              setOgImageFile(null);
              setOgImagePreview('');
              handleInputChange('ogImage', '');
            }}
          />
        );

      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <SettingsModal
      isOpen={isOpen}
      onClose={onClose}
      title="Requests Marketplace Settings"
      subtitle="Configure your request marketplace template"
      onSave={handleSave}
      saving={saving}
      hasChanges={hasChanges}
    >
      <div className="flex h-full overflow-hidden">
        <TabNavigation
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      </div>
      <div className="flex-1 overflow-y-auto min-h-0">
        <div className="p-6">
          {renderTabContent()}
        </div>
      </div>
    </SettingsModal>
  );
};

export default RequestsSettings;

const RequestsConfigTab = ({ formData, onInputChange }) => {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Request Configuration</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Request Placeholder Text
            </label>
            <input
              type="text"
              value={formData.requestsSettings?.requestPlaceholder || ''}
              onChange={(e) => onInputChange('requestsSettings.requestPlaceholder', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Describe what you need…"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default Maximum Responses
            </label>
            <input
              type="number"
              min={1}
              max={20}
              value={formData.requestsSettings?.defaultMaxResponses || 3}
              onChange={(e) => onInputChange('requestsSettings.defaultMaxResponses', Number(e.target.value))}
              className="w-32 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-sm text-gray-500 mt-1">Maximum responses per request</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Provider Visibility
            </label>
            <select
              value={formData.requestsSettings?.providerVisibility || 'all-providers'}
              onChange={(e) => onInputChange('requestsSettings.providerVisibility', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all-providers">All Providers</option>
              <option value="verified-only">Verified Providers Only</option>
              <option value="featured-only">Featured Providers Only</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              AI Seed Prompt (Optional)
            </label>
            <textarea
              rows={4}
              value={formData.requestsSettings?.aiSeedPrompt || ''}
              onChange={(e) => onInputChange('requestsSettings.aiSeedPrompt', e.target.value)}
              placeholder="Optional: seed prompt for AI-generated offers…"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-sm text-gray-500 mt-1">Use AI to help generate initial responses</p>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Feature Settings</h3>
        
        <div className="space-y-4">
          {[
            { 
              key: 'enableAutoMatching', 
              label: 'Auto-Match Providers', 
              desc: 'Automatically suggest providers based on request criteria' 
            },
            { 
              key: 'requireProviderApproval', 
              label: 'Require Provider Approval', 
              desc: 'Providers must be approved before responding to requests' 
            },
            { 
              key: 'allowPublicRequests', 
              label: 'Allow Public Requests', 
              desc: 'Requests can be viewed by non-authenticated users' 
            },
            { 
              key: 'enableRatings', 
              label: 'Enable Ratings', 
              desc: 'Allow users to rate providers' 
            },
            { 
              key: 'enableReviews', 
              label: 'Enable Reviews', 
              desc: 'Allow users to write reviews for providers' 
            },
            { 
              key: 'notifyOnNewRequest', 
              label: 'Notify on New Request', 
              desc: 'Send notifications when new requests are created' 
            },
            { 
              key: 'notifyOnNewResponse', 
              label: 'Notify on New Response', 
              desc: 'Send notifications when providers respond' 
            }
          ].map(({ key, label, desc }) => (
            <label 
              key={key} 
              className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <input
                type="checkbox"
                checked={formData.requestsSettings?.[key] ?? true}
                onChange={(e) => onInputChange(`requestsSettings.${key}`, e.target.checked)}
                className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <div className="flex-1">
                <div className="font-medium text-gray-900">{label}</div>
                <div className="text-sm text-gray-600">{desc}</div>
              </div>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
};

// src/components/requests/RequestsSettings.jsx - UPDATE ProviderConfigTab component

const ProviderConfigTab = ({ formData, onInputChange, setFormData }) => {
  const [editingField, setEditingField] = useState(null);
  const [newCategory, setNewCategory] = useState('');

  const addField = () => {
    const newField = {
      id: `field_${Date.now()}`,
      name: '',
      type: 'text',
      label: '',
      required: false,
      order: formData.providerFields?.length || 0
    };
    
    onInputChange('providerFields', [...(formData.providerFields || []), newField]);
    setEditingField(newField.id);
  };

  const updateField = (fieldId, updates) => {
    const updatedFields = formData.providerFields.map(f => 
      f.id === fieldId ? { ...f, ...updates } : f
    );
    onInputChange('providerFields', updatedFields);
  };

  const removeField = (fieldId) => {
    if (!window.confirm('Remove this field?')) return;
    const updatedFields = formData.providerFields.filter(f => f.id !== fieldId);
    onInputChange('providerFields', updatedFields);
  };

  const addCategory = () => {
    if (!newCategory.trim()) return;
    onInputChange('providerCategories', [...(formData.providerCategories || []), newCategory.trim()]);
    setNewCategory('');
  };

  const removeCategory = (category) => {
    if (!window.confirm(`Remove "${category}"?`)) return;
    const updatedCategories = formData.providerCategories.filter(c => c !== category);
    onInputChange('providerCategories', updatedCategories);
  };

  const updateProviderSetting = (key, value) => {
    onInputChange('providerSettings', { ...formData.providerSettings, [key]: value });
  };

  const updateCreditsSetting = (key, value) => {
    onInputChange('creditsSettings', { 
      ...(formData.creditsSettings || {}), 
      [key]: value 
    });
  };

  return (
    <div className="space-y-8">
      
      {/* Registration Fields */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-lg">Registration Fields</h3>
            <p className="text-sm text-slate-500">Configure fields shown during provider registration</p>
          </div>
          <button
            onClick={addField}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-slate-100 dark:bg-slate-800 rounded-lg hover:bg-slate-200"
          >
            <Plus className="h-4 w-4" />
            Add Field
          </button>
        </div>

        <div className="space-y-2">
          {formData.providerFields?.sort((a, b) => a.order - b.order).map(field => (
            <div
              key={field.id}
              className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700"
            >
              {editingField === field.id ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      placeholder="Field Name (e.g., businessName)"
                      value={field.name}
                      onChange={(e) => updateField(field.id, { name: e.target.value })}
                      className="px-3 py-2 border rounded-lg text-sm bg-white dark:bg-slate-900"
                    />
                    <input
                      type="text"
                      placeholder="Label (e.g., Business Name)"
                      value={field.label}
                      onChange={(e) => updateField(field.id, { label: e.target.value })}
                      className="px-3 py-2 border rounded-lg text-sm bg-white dark:bg-slate-900"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      value={field.type}
                      onChange={(e) => updateField(field.id, { type: e.target.value })}
                      className="px-3 py-2 border rounded-lg text-sm bg-white dark:bg-slate-900"
                    >
                      <option value="text">Text</option>
                      <option value="textarea">Text Area</option>
                      <option value="email">Email</option>
                      <option value="tel">Phone</option>
                      <option value="url">URL</option>
                      <option value="select">Select</option>
                      <option value="image">Image</option>
                      <option value="number">Number</option>
                    </select>
                    <input
                      type="text"
                      placeholder="Placeholder text"
                      value={field.placeholder || ''}
                      onChange={(e) => updateField(field.id, { placeholder: e.target.value })}
                      className="px-3 py-2 border rounded-lg text-sm bg-white dark:bg-slate-900"
                    />
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={field.required}
                        onChange={(e) => updateField(field.id, { required: e.target.checked })}
                        className="rounded"
                      />
                      Required
                    </label>
                    <button
                      onClick={() => setEditingField(null)}
                      className="ml-auto px-3 py-1 text-sm bg-slate-900 text-white rounded-lg"
                    >
                      Done
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <GripVertical className="h-4 w-4 text-slate-400" />
                    <div>
                      <div className="font-medium">
                        {field.label}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                      </div>
                      <div className="text-xs text-slate-500">{field.name} • {field.type}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setEditingField(field.id)}
                      className="p-1.5 text-slate-600 hover:bg-slate-100 rounded"
                    >
                      <Settings2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => removeField(field.id)}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Provider Categories */}
      <div>
        <div className="mb-4">
          <h3 className="font-semibold text-lg">Provider Categories</h3>
          <p className="text-sm text-slate-500">Categories for provider classification</p>
        </div>

        <div className="flex gap-2 mb-3">
          <input
            type="text"
            placeholder="Add category"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && addCategory()}
            className="flex-1 px-3 py-2 border rounded-lg text-sm bg-white dark:bg-slate-900"
          />
          <button
            onClick={addCategory}
            className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm"
          >
            Add
          </button>
        </div>

        <div className="space-y-2">
          {formData.providerCategories?.map(category => (
            <div
              key={category}
              className="flex items-center justify-between p-2 bg-white dark:bg-slate-800 rounded-lg border"
            >
              <span className="text-sm">{category}</span>
              <button
                onClick={() => removeCategory(category)}
                className="p-1 text-red-600 hover:bg-red-50 rounded"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* NEW: Credits & Pricing Configuration */}
      <div>
        <div className="mb-4">
          <h3 className="font-semibold text-lg">Credits & Pricing</h3>
          <p className="text-sm text-slate-500">Configure credit costs and starter packages</p>
        </div>

        <div className="space-y-4">
          {/* Offer Costs */}
          <div className="p-4 bg-white dark:bg-slate-800 rounded-lg border">
            <h4 className="font-medium text-sm mb-3">Usage Rates</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Cost Per Offer</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="1"
                    value={formData.creditsSettings?.costPerOffer || 5}
                    onChange={(e) => updateCreditsSetting('costPerOffer', parseInt(e.target.value) || 5)}
                    className="w-24 px-3 py-2 border rounded-lg text-sm bg-white dark:bg-slate-900"
                  />
                  <span className="text-sm text-slate-500">credits</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Cost Per Response</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    value={formData.creditsSettings?.costPerResponse || 1}
                    onChange={(e) => updateCreditsSetting('costPerResponse', parseInt(e.target.value) || 1)}
                    className="w-24 px-3 py-2 border rounded-lg text-sm bg-white dark:bg-slate-900"
                  />
                  <span className="text-sm text-slate-500">credits</span>
                </div>
              </div>
            </div>
          </div>

          {/* Free Credits */}
          <div className="p-4 bg-white dark:bg-slate-800 rounded-lg border">
            <h4 className="font-medium text-sm mb-3">Starter Package</h4>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-2">Free Credits on Registration</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    value={formData.creditsSettings?.freeCredits || 50}
                    onChange={(e) => updateCreditsSetting('freeCredits', parseInt(e.target.value) || 50)}
                    className="w-24 px-3 py-2 border rounded-lg text-sm bg-white dark:bg-slate-900"
                  />
                  <span className="text-sm text-slate-500">credits</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Equivalent to ~{Math.floor((formData.creditsSettings?.freeCredits || 50) / (formData.creditsSettings?.costPerOffer || 5))} offers
                </p>
              </div>
              
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.creditsSettings?.allowAdditionalPurchase !== false}
                  onChange={(e) => updateCreditsSetting('allowAdditionalPurchase', e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm">Allow providers to purchase additional credits</span>
              </label>
            </div>
          </div>

          {/* Credit Packages */}
          {formData.creditsSettings?.allowAdditionalPurchase !== false && (
            <div className="p-4 bg-white dark:bg-slate-800 rounded-lg border">
              <h4 className="font-medium text-sm mb-3">Credit Packages</h4>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Basic</label>
                  <div className="flex flex-col gap-1">
                    <input
                      type="number"
                      placeholder="Credits"
                      value={formData.creditsSettings?.packages?.basic?.credits || 100}
                      onChange={(e) => updateCreditsSetting('packages', {
                        ...(formData.creditsSettings?.packages || {}),
                        basic: { 
                          ...(formData.creditsSettings?.packages?.basic || {}),
                          credits: parseInt(e.target.value) || 100 
                        }
                      })}
                      className="w-full px-2 py-1 border rounded text-sm bg-white dark:bg-slate-900"
                    />
                    <input
                      type="number"
                      placeholder="Price (R)"
                      value={formData.creditsSettings?.packages?.basic?.price || 100}
                      onChange={(e) => updateCreditsSetting('packages', {
                        ...(formData.creditsSettings?.packages || {}),
                        basic: { 
                          ...(formData.creditsSettings?.packages?.basic || {}),
                          price: parseInt(e.target.value) || 100 
                        }
                      })}
                      className="w-full px-2 py-1 border rounded text-sm bg-white dark:bg-slate-900"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Standard</label>
                  <div className="flex flex-col gap-1">
                    <input
                      type="number"
                      placeholder="Credits"
                      value={formData.creditsSettings?.packages?.standard?.credits || 250}
                      onChange={(e) => updateCreditsSetting('packages', {
                        ...(formData.creditsSettings?.packages || {}),
                        standard: { 
                          ...(formData.creditsSettings?.packages?.standard || {}),
                          credits: parseInt(e.target.value) || 250 
                        }
                      })}
                      className="w-full px-2 py-1 border rounded text-sm bg-white dark:bg-slate-900"
                    />
                    <input
                      type="number"
                      placeholder="Price (R)"
                      value={formData.creditsSettings?.packages?.standard?.price || 225}
                      onChange={(e) => updateCreditsSetting('packages', {
                        ...(formData.creditsSettings?.packages || {}),
                        standard: { 
                          ...(formData.creditsSettings?.packages?.standard || {}),
                          price: parseInt(e.target.value) || 225 
                        }
                      })}
                      className="w-full px-2 py-1 border rounded text-sm bg-white dark:bg-slate-900"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Premium</label>
                  <div className="flex flex-col gap-1">
                    <input
                      type="number"
                      placeholder="Credits"
                      value={formData.creditsSettings?.packages?.premium?.credits || 500}
                      onChange={(e) => updateCreditsSetting('packages', {
                        ...(formData.creditsSettings?.packages || {}),
                        premium: { 
                          ...(formData.creditsSettings?.packages?.premium || {}),
                          credits: parseInt(e.target.value) || 500 
                        }
                      })}
                      className="w-full px-2 py-1 border rounded text-sm bg-white dark:bg-slate-900"
                    />
                    <input
                      type="number"
                      placeholder="Price (R)"
                      value={formData.creditsSettings?.packages?.premium?.price || 400}
                      onChange={(e) => updateCreditsSetting('packages', {
                        ...(formData.creditsSettings?.packages || {}),
                        premium: { 
                          ...(formData.creditsSettings?.packages?.premium || {}),
                          price: parseInt(e.target.value) || 400 
                        }
                      })}
                      className="w-full px-2 py-1 border rounded text-sm bg-white dark:bg-slate-900"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Provider Settings */}
      <div>
        <div className="mb-4">
          <h3 className="font-semibold text-lg">Provider Settings</h3>
          <p className="text-sm text-slate-500">Configure provider management options</p>
        </div>

        <div className="space-y-3">
          <label className="flex items-center justify-between p-3 bg-white dark:bg-slate-800 rounded-lg border">
            <div>
              <div className="font-medium text-sm">Require Approval</div>
              <div className="text-xs text-slate-500">Providers need approval before appearing</div>
            </div>
            <input
              type="checkbox"
              checked={formData.providerSettings?.requireApproval}
              onChange={(e) => updateProviderSetting('requireApproval', e.target.checked)}
              className="w-5 h-5 rounded"
            />
          </label>

          <label className="flex items-center justify-between p-3 bg-white dark:bg-slate-800 rounded-lg border">
            <div>
              <div className="font-medium text-sm">Enable Verified Badge</div>
              <div className="text-xs text-slate-500">Show verified badge for approved providers</div>
            </div>
            <input
              type="checkbox"
              checked={formData.providerSettings?.enableVerifiedBadge}
              onChange={(e) => updateProviderSetting('enableVerifiedBadge', e.target.checked)}
              className="w-5 h-5 rounded"
            />
          </label>

          <label className="flex items-center justify-between p-3 bg-white dark:bg-slate-800 rounded-lg border">
            <div>
              <div className="font-medium text-sm">Enable Featured</div>
              <div className="text-xs text-slate-500">Allow featuring providers at top</div>
            </div>
            <input
              type="checkbox"
              checked={formData.providerSettings?.enableFeatured}
              onChange={(e) => updateProviderSetting('enableFeatured', e.target.checked)}
              className="w-5 h-5 rounded"
            />
          </label>

          <label className="flex items-center justify-between p-3 bg-white dark:bg-slate-800 rounded-lg border">
            <div>
              <div className="font-medium text-sm">Allow Multiple Listings</div>
              <div className="text-xs text-slate-500">Let providers create multiple profiles</div>
            </div>
            <input
              type="checkbox"
              checked={formData.providerSettings?.allowMultipleListings}
              onChange={(e) => updateProviderSetting('allowMultipleListings', e.target.checked)}
              className="w-5 h-5 rounded"
            />
          </label>

          <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border">
            <div className="font-medium text-sm mb-2">Max Listings Per Provider</div>
            <input
              type="number"
              min="0"
              value={formData.providerSettings?.maxListingsPerProvider || 0}
              onChange={(e) => updateProviderSetting('maxListingsPerProvider', parseInt(e.target.value) || 0)}
              className="w-24 px-3 py-2 border rounded-lg text-sm bg-white dark:bg-slate-900"
            />
            <div className="text-xs text-slate-500 mt-1">0 = unlimited</div>
          </div>
        </div>
      </div>

    </div>
  );
};